import React, { useCallback, useEffect, useState } from "react";
import { withStyles } from "@material-ui/styles";
import { connect } from "react-redux";
import { I18n } from "@aws-amplify/core";
import update from "immutability-helper";
import {
  debounce,
  get,
  map,
  size,
  pickBy,
  filter,
  includes,
  toLower,
  trim,
} from "lodash";
import Grid from "@material-ui/core/Grid";
import Dialog from "@material-ui/core/Dialog";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import { fetchDelete, fetchGet, fetchPost, fetchPut } from "../../api/fetch";
import AddIcon from "../Common/AddIcon";
import CloseIcon from "../Common/CloseIcon";
import CustomButton from "../Common/CustomButton";
import FormSection from "../Common/FormSection";
import { getLov } from "../../utils/lov";
import { validateEmail } from "../../utils/form";
import { isAllowed } from "../../utils/users";
import { roles as rolesLevels } from "../../constants/roles";
import colors from "../../assets/style/colors";
import { CreateUserFromProfile } from "./CreateUserFromProfile";
import { CreateNewUser } from "./CreateNewUser";

const styles = {
  dialogRoot: {
    zIndex: "800 !important",
  },
  dialog: {
    maxWidth: "400px",
    minWidth: "400px",
    maxHeight: "calc(100vh - 60px)",
    minHeight: "300px",
  },
  closeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingRight: "12px",
    "& img": {
      cursor: "pointer",
    },
  },
  dialogTitle: {
    fontSize: "1em",
    margin: "20px",
    color: colors.black54,
  },
  postCreationBtn: {
    margin: "10px 0px",
  },
  titleBarItem: {
    display: "flex",
    alignItems: "center",
  },
  menuItem: {
    padding: "15px 30px",
    fontSize: "1em",
    "&:hover": {
      backgroundColor: colors.lightGreenBg,
    },
  },
  itemIcon: {
    width: "35px",
    minWidth: "unset",
    marginRight: "15px",
  },
  radio1: {
    fontWeight: 500,
    marginRight: "40px",
    fontSize: "1em",
  },
  radioRoot: {
    margin: "0 !important",
  },
};

const creationTypes = {
  new: "new",
  fromProfile: "fromProfile",
};

const UserModal = (props) => {
  const {
    classes,
    language,
    user,
    editedUser,
    isEditing,
    setIsOpen,
    isOpen,
    onUserUpdated,
  } = props;

  const [creationType, setCreationType] = useState(creationTypes.new);
  const [lov, setLov] = useState({});

  const [values, setValues] = useState({});
  const [valid, setValid] = useState({
    firstname: false,
    lastname: false,
    username: false,
    email: false,
    roles: false,
    tag: false,
    35: false,
    company_id: false,
  });
  const [errors, setErrors] = useState({});
  const [selectedProfile, setSelectedProfile] = useState();
  const [initialEditedUser, setInitialEditedUser] = useState(null);

  useEffect(() => {
    fetchLov();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only initialize once per edited user
    if (isEditing && editedUser && !initialEditedUser) {
      const initialValues = {
        roles: (editedUser.roles || []).map((role) => role.id),
      };

      setValues(initialValues);
      setInitialEditedUser(initialValues);

      setValid((previous) => ({
        ...previous,
        roles: initialValues.roles.length > 0,
      }));
    }
  }, [isEditing, editedUser, initialEditedUser]);

  const fetchLov = async (newValid) => {
    let roles = await getLov("roles");

    if (get(user, ["role", "level"]) > rolesLevels.jrvAdmin) {
      roles = filter(
        roles,
        (r) => !includes(toLower(get(r, ["name_en"])), "jrv admin")
      );
    }
    setLov({
      roles,
      tags: await getLov("tags"),
      35: await getLov(35),
      companies: get(user, ["companies"]),
    });

    if (size(get(user, ["companies"])) === 1 && !isEditing) {
      setValues((previous) => ({
        ...previous,
        company_id: get(user, ["companies", 0, "id"]),
      }));
      setValid((previous) =>
        update(newValid ? newValid : previous, {
          company_id: { $set: true },
        })
      );
    }
  };

  const resetForm = () => {
    setCreationType(creationTypes.new);
    setValues({});
    setErrors({});
    setSelectedProfile();
    fetchLov({
      firstname: false,
      lastname: false,
      username: false,
      email: false,
      roles: false,
      tag: false,
      35: false,
      company_id: false,
    });
  };

  const canSubmit = () => {
    if (isEditing) {
      return valid.roles && size(values.roles || []) > 0;
    }

    if (creationType === creationTypes.new) {
      return size(pickBy(valid, (v) => !v)) === 0;
    } else if (creationType === creationTypes.fromProfile) {
      return Boolean(selectedProfile) && valid.username && valid.roles;
    }
  };

  const handleChange = (id, _, value) => {
    let newValues = update(values, {
      [id]: { $set: value },
    });

    validation(newValues, id);
    setValues(newValues);
  };

  const validation = (newFormValues, field) => {
    debouncedValidation(newFormValues, valid, errors, field);
  };

  const debouncedValidation = useCallback(
    debounce(async (newFormValues, newValid, newErrors, field) => {
      if (field) {
        [newValid, newErrors] = await validateField(
          newFormValues,
          newValid,
          newErrors,
          field
        );
        setValid(newValid);
        setErrors(newErrors);
        return newValid;
      }
    }, 500),
    []
  );

  const validateField = async (newValues, newValid, newErrors, id) => {
    let isValid = false;
    let error;
    const value = get(newValues, [id]);

    if (["firstname", "lastname"].includes(id)) {
      if (value !== "") {
        isValid = true;
      } else {
        error = I18n.get("users_requiredField");
      }
    } else if (id === "username") {
      if (value === "") {
        error = I18n.get("users_requiredField");
      } else if (
        isEditing &&
        initialEditedUser &&
        value === initialEditedUser.username
      ) {
        isValid = true;
      } else {
        try {
          await fetchGet(`users/username/${value}`);
          error = I18n.get("users_usernameAlreadyInUse");
        } catch (e) {
          isValid = true;
        }
      }
    } else if (id === "email") {
      if (value === "") {
        error = I18n.get("users_requiredField");
      } else if (!validateEmail(value)) {
        error = I18n.get("users_incorrectEmail");
      } else if (
        isEditing &&
        initialEditedUser &&
        value === initialEditedUser.email
      ) {
        isValid = true;
      } else {
        try {
          const response = await fetchPost("profiles/search/email", {
            email: value,
            profile_type: 1,
          });

          if (response.count > 0) {
            error = I18n.get("profiles_emailAlreadyInUse");
          } else {
            isValid = true;
          }
        } catch (e) {
          console.log(e);
        }
      }
    } else if (id === "roles") {
      if (size(value) > 0) {
        isValid = true;
      } else {
        error = I18n.get("users_requiredField");
      }
    } else if (includes(["tag", 35, "company_id"], id)) {
      if (value) {
        isValid = true;
      } else {
        error = I18n.get("users_requiredField");
      }
    } else if (id === "site_id") {
      isValid = true;
    }

    return [
      update(newValid, {
        [id]: { $set: isValid },
      }),
      update(newErrors, {
        [id]: { $set: error },
      }),
    ];
  };

  const closeDialog = () => {
    setIsOpen(false);
    resetForm();
    if (props.onClose) {
      props.onClose();
    }
  };

  const submit = async () => {
    const roles = get(values, ["roles"], []);

    if (size(roles) === 0) {
      setValid((prev) =>
        update(prev, {
          roles: { $set: false },
        })
      );
      setErrors((prev) =>
        update(prev, {
          roles: { $set: I18n.get("users_requiredField") },
        })
      );

      return;
    }

    try {
      if (isEditing && editedUser) {
        const initialRoleIds = (initialEditedUser?.roles || []).slice();
        const newRoleIds = roles || [];

        const rolesToAdd = newRoleIds.filter(
          (roleId) => !initialRoleIds.includes(roleId)
        );
        const rolesToRemove = initialRoleIds.filter(
          (roleId) => !newRoleIds.includes(roleId)
        );

        for (const roleId of rolesToAdd) {
          await fetchPut(`users/${editedUser.id}/roles/${roleId}`);
        }

        for (const roleId of rolesToRemove) {
          await fetchDelete(`users/${editedUser.id}/roles/${roleId}`);
        }

        if (typeof onUserUpdated === "function") {
          await onUserUpdated();
        }
      } else {
        const savedUser = await fetchPost("users", {
          firstname: get(values, ["firstname"]),
          lastname: get(values, ["lastname"]),
          username: get(values, ["username"]),
          email: toLower(trim(get(values, ["email"]))),
          tag_id: get(values, ["tag"]),
          profile_id: get(selectedProfile, ["id"]),
          language_id: get(values, [35]),
          company_id: get(values, ["company_id"]),
          site_id: get(values, ["site_id"]),
        });

        for (let index = 0; index < size(roles); index++) {
          await fetchPut(
            `users/${get(savedUser, ["id"])}/roles/${roles[index]}`
          );
        }
      }
    } catch (error) {
      console.log(error);
    }

    closeDialog();
  };

  return (
    <>
      {!isEditing && (
        <AddIcon
          subMenu={[
            { name: "single_user", element: I18n.get("users_singleUser") },
          ]}
          labelField="element"
          handleSubMenuSelect={(element) => setIsOpen(get(element, ["name"]))}
        />
      )}
      <Dialog
        open={Boolean(isOpen)}
        classes={{
          root: classes.dialogRoot,
          paperScrollPaper: classes.dialog,
        }}
      >
        <Grid
          container
          wrap="nowrap"
          direction="column"
          alignItems="stretch"
          style={{ height: "100%", padding: "12px 0px" }}
        >
          <Grid item style={{ marginBottom: "10px" }}>
            <Grid container alignItems="center">
              <Grid item xs={2} />
              <Grid
                item
                xs
                className="dialogTitle flexCenter"
                style={{ justifyContent: "center" }}
              >
                {I18n.get(isEditing ? "users_updateUserRole" : "users_newUser")}
              </Grid>
              <Grid item xs={2} className={classes.closeBtn}>
                <CloseIcon closeFunction={closeDialog} />
              </Grid>
            </Grid>
          </Grid>
          {!isEditing && (
            <Grid
              item
              style={{
                marginBottom: "20px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <RadioGroup
                row
                name="type"
                value={creationType}
                onChange={(event) => setCreationType(event.target.value)}
              >
                {map(creationTypes, (type) => (
                  <FormControlLabel
                    key={type}
                    value={type}
                    control={<Radio color="primary" />}
                    label={I18n.get(`users_${type}`)}
                    labelPlacement="end"
                    classes={{ label: classes.radio1, root: classes.radioRoot }}
                  />
                ))}
              </RadioGroup>
            </Grid>
          )}
          <Grid item style={{ padding: "0px 70px" }}>
            {creationType === creationTypes.new || isEditing ? (
              <CreateNewUser
                isEditing={isEditing}
                language={language}
                values={values}
                errors={errors}
                valid={valid}
                lov={lov}
                handleChange={handleChange}
              />
            ) : (
              <CreateUserFromProfile
                selectedProfile={selectedProfile}
                language={language}
                values={values}
                errors={errors}
                valid={valid}
                lov={lov}
                setSelectedProfile={setSelectedProfile}
                handleChange={handleChange}
              />
            )}
            {!isEditing && (
              <>
                <FormSection
                  formValue={[get(values, ["company_id"]) || ""]}
                  setFormValue={handleChange}
                  title="assignedCompany"
                  id="company_id"
                  type="select"
                  options={{
                    disabled: !isAllowed(
                      get(user, ["permissions"]),
                      "access.cross.companies.admin"
                    ),
                    required: true,
                    redBorder: !get(valid, ["company_id"]),
                    errorMsg: errors["company_id"],
                    options: map(get(lov, ["companies"]), (company) => ({
                      id: company.id,
                      name: company.name,
                    })),
                  }}
                />
                <FormSection
                  formValue={[get(values, ["site_id"]) || ""]}
                  setFormValue={handleChange}
                  title="assignedSite"
                  id="site_id"
                  type="select"
                  options={{
                    disabled: !isAllowed(
                      get(user, ["permissions"]),
                      "access.cross.sites.admin"
                    ),
                    options: map(
                      get(
                        filter(
                          get(lov, ["companies"]),
                          (company) =>
                            company.id === get(values, ["company_id"])
                        ),
                        [0, "sites"]
                      ),
                      (t) => ({
                        id: t.id,
                        name: t.name,
                      })
                    ),
                  }}
                />
              </>
            )}
          </Grid>
          <Grid item className="text-center">
            <CustomButton
              variant="contained"
              color="primary"
              onClick={submit}
              disabled={!canSubmit()}
              style={{ margin: "40px 0px 50px 0px" }}
            >
              {I18n.get(isEditing ? "account_update" : "users_createAndInvite")}
            </CustomButton>
          </Grid>
        </Grid>
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    language: state.user.language,
    user: state.user,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(UserModal));
